import type { AdState } from '|>/core/addons/smartlib'

import { Events } from '|>/shared/events'
import { bind_, throttle, UPDATE_REFRESH_INTERVAL } from '|>/shared/fn'
import { on, register } from '|>/shared/vjs'
import { BaseComponent, BasePlayProgressBar } from '../base'

import './ad-progress-control.css'

/**
 * Ad Progress Control
 * Displays progress bar for current ad (non-interactive)
 */
@register
export class AdProgressControl extends BaseComponent {
  declare playProgressBar: typeof BasePlayProgressBar

  @on(Events.Media.AdStateChanged)
  private adState: AdState | null = null

  private updateInterval: ReturnType<typeof setInterval> | null = null

  constructor(player, options) {
    super(player, options)
    this.hide()

    this.updateForced = bind_(this, this.update)
    this.update = throttle(this.updateForced, UPDATE_REFRESH_INTERVAL)

    // Listen to timeupdate to update progress
    this.player_.on('timeupdate', this.update)
  }

  override createEl() {
    return super.createEl('div', { className: 'vjs-progress-holder' })
  }

  @on(Events.Media.AdStateChanged)
  handleAdStateChange(adState: AdState) {
    this.adState = adState

    if (adState.isAdPlaying && adState.currentAd) {
      this.show()
      this.updateForced()
      this.enableInterval()
    } else {
      this.hide()
      this.disableInterval()
    }
  }

  private enableInterval() {
    if (this.updateInterval) return

    this.updateInterval = setInterval(this.update, UPDATE_REFRESH_INTERVAL)
  }

  private disableInterval() {
    if (!this.updateInterval) return

    clearInterval(this.updateInterval)
    this.updateInterval = null
  }

  /**
   * Calculate ad progress percentage (0-1)
   */
  private getAdProgress(): number {
    if (!this.adState?.currentAd) return 0

    const currentTime = this.player_.currentTime()
    const adStart = this.adState.currentAd.startPosition
    const adDuration = this.adState.currentAd.duration
    const adEnd = adStart + adDuration

    if (currentTime < adStart) return 0
    if (currentTime >= adEnd) return 1

    const progress = (currentTime - adStart) / adDuration
    return Math.min(Math.max(progress, 0), 1)
  }

  updateForced: typeof this.update
  update() {
    if (document.visibilityState === 'hidden') return

    if (!this.adState?.isAdPlaying || !this.adState.currentAd) {
      return
    }

    this.requestNamedAnimationFrame('AdProgressControl#update', () => {
      const progress = this.getAdProgress()

      // Update progress bar width via child component
      const playProgressBar = this.getChild('playProgressBar')
      if (playProgressBar) {
        const el = playProgressBar.el() as HTMLElement
        if (el) {
          el.style.width = `${progress * 100}%`
        }
      }
    })
  }

  override dispose() {
    this.disableInterval()
    this.player_.off('timeupdate', this.update)
    super.dispose()
  }
}

AdProgressControl.options = {
  className: 'vjs-x-ad-progress-control',
  children: [BasePlayProgressBar.as('playProgressBar')],
}
